/**
 * Получатель открывает ссылку https://sd.devkz.ru/secret/<id>#<key_b64>
 * - id передаётся в pathname
 * - key (256-bit) — в URL fragment (на сервер не отправляется)
 *
 * Flow:
 *  1. Тянем GET /api/v1/secrets/<id> — получаем ciphertext+nonce (AES-256-GCM).
 *  2. Расшифровываем через Web Crypto API в браузере.
 *  3. Показываем plaintext + предупреждение «N просмотров осталось».
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ShieldAlert, Copy, Check, Loader2, AlertTriangle } from 'lucide-react';

interface ServerSecret {
  ciphertext_b64: string;
  nonce_b64: string;
  label: string | null;
  views_left: number;
  expires_at: string;
}

function b64urlDecode(s: string): Uint8Array {
  const norm = s.replace(/-/g, '+').replace(/_/g, '/');
  const pad = (4 - (norm.length % 4)) % 4;
  const bin = atob(norm + '='.repeat(pad));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export default function SecretViewPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [status, setStatus] = useState<'loading' | 'shown' | 'error'>('loading');
  const [plaintext, setPlaintext] = useState('');
  const [meta, setMeta] = useState<ServerSecret | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      const fragment = window.location.hash.slice(1);
      if (!fragment) {
        setError('В ссылке нет ключа (после #). Не удастся расшифровать.');
        setStatus('error'); return;
      }
      try {
        const r = await fetch(`/api/v1/secrets/${id}`);
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          setError(d.detail || `HTTP ${r.status}`);
          setStatus('error'); return;
        }
        const data: ServerSecret = await r.json();
        const keyBytes = b64urlDecode(fragment);
        if (keyBytes.length !== 32) {
          setError('Ключ в ссылке некорректной длины.');
          setStatus('error'); return;
        }
        const key = await crypto.subtle.importKey(
          'raw', keyBytes.buffer as ArrayBuffer, { name: 'AES-GCM' }, false, ['decrypt'],
        );
        const ct = b64urlDecode(data.ciphertext_b64);
        const nonce = b64urlDecode(data.nonce_b64);
        const plain = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: nonce.buffer as ArrayBuffer }, key,
          ct.buffer as ArrayBuffer,
        );
        const text = new TextDecoder().decode(plain);
        setMeta(data);
        setPlaintext(text);
        setStatus('shown');
      } catch (e: any) {
        setError(`Не удалось расшифровать: ${e?.message || 'ошибка'}`);
        setStatus('error');
      }
    })();
  }, [id]);

  const copy = () => {
    navigator.clipboard.writeText(plaintext).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
        <header className="flex items-center gap-2 mb-3 text-amber-400">
          <ShieldAlert size={20} />
          <h1 className="text-lg font-bold text-white">Защищённое сообщение</h1>
        </header>

        {status === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-6">
            <Loader2 className="animate-spin" size={16} /> Расшифровываем…
          </div>
        )}

        {status === 'error' && (
          <div className="bg-red-900/30 border border-red-800 text-red-200 px-3 py-3 rounded text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <b>Не удалось показать секрет.</b>
              <div className="mt-1 text-xs">{error}</div>
              <div className="mt-2 text-xs text-slate-400">
                Возможные причины: ссылка уже была использована, истёк срок жизни,
                ключ в URL некорректный или был обрезан.
              </div>
            </div>
          </div>
        )}

        {status === 'shown' && meta && (
          <>
            {meta.label && (
              <div className="text-xs text-slate-400 mb-1">
                Метка: <b className="text-slate-200">{meta.label}</b>
              </div>
            )}
            <pre className="bg-slate-950 border border-slate-700 rounded p-3 text-sm text-emerald-200 font-mono whitespace-pre-wrap break-all min-h-[80px]">
              {plaintext}
            </pre>
            <div className="mt-2 flex justify-between items-center">
              <button onClick={copy}
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded text-xs">
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? 'Скопировано' : 'Скопировать'}
              </button>
              <span className="text-[10px] text-slate-500">
                Просмотров осталось: {meta.views_left} · истекает {new Date(meta.expires_at).toLocaleString()}
              </span>
            </div>
            <p className="text-[10px] text-slate-600 mt-3">
              Ключ расшифровки получен только в вашем браузере (из URL после #) и
              не отправляется на сервер. После исчерпания просмотров запись
              автоматически удалится.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
